// filepath: apps/web/src/routes/ui-test.tsx
//
// /ui-test — visual verification of the @osd/ui design system.
// Loads components via the workspace dep (not the self-hosted
// browser pack — that's the docs link, not this route). Useful
// for confirming a new design-system build renders the way we
// expect before opening the live site.

import { createFileRoute } from '@tanstack/react-router';
import { Alert } from '@osd/ui/alert';
import { Button } from '@osd/ui/button';
import { Card } from '@osd/ui/card';
import { Heading } from '@osd/ui/heading';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';

function UiTestPage(): JSX.Element {
  return (
    <Stack gap={4}>
      <header>
        <Heading level={1}>@osd/ui — visual smoke</Heading>
        <Text muted>
          Loaded via the workspace dependency (the same import path as every page in the app). For
          the self-hosted browser pack demo (plain DOM + browser ESM resolver), see{' '}
          <a href="https://bugfix.pro/_ui/test.html" target="_blank" rel="noreferrer">
            /_ui/test.html
          </a>
          .
        </Text>
      </header>

      <Alert variant="success" title="Loaded from @osd/ui">
        If you can read this, the React side of the design system is rendering. The browser-pack
        demo (no React app, plain DOM) is at the link above.
      </Alert>

      <Card>
        <Stack gap={3}>
          <Heading level={2}>Buttons</Heading>
          <Stack direction="row" gap={2}>
            <Button variant="primary">Primary</Button>
            <Button>Default</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="danger">Danger</Button>
          </Stack>
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <Heading level={2}>Alerts</Heading>
          <Stack gap={2}>
            <Alert variant="info" title="Info">
              Informational message.
            </Alert>
            <Alert variant="success" title="Success">
              Operation completed.
            </Alert>
            <Alert variant="warning" title="Warning">
              Heads up — review the details.
            </Alert>
            <Alert variant="danger" title="Danger">
              Something failed.
            </Alert>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}

export const Route = createFileRoute('/ui-test')({
  component: UiTestPage,
});
